// Extract structured bank fields from a free-form 'bank_details' string.
//
// Used in three places that need to read the legacy single-blob field
// (saved by older landlord builds) and render it as 4 separate rows:
//   - SettingsPage.js   — pre-fills new structured inputs from the blob
//   - TenPayNow.js      — bank-transfer copy buttons on the Pay Now page
//   - TenDash.js        — bank-transfer copy buttons in the inline panel
//
// Patterns handled:
//   "Account Name: X, A/C No: 12345, IFSC: ABCD0001, Bank: Foo Bank"
//   "Acc 12345, IFSC ABCD0001, Bank Foo"
//   "Acct No 12345  IFSC: ABCD0001  Bank Foo Bank"
//   etc. (case insensitive, separator-tolerant)
//
// Returns { account_name, account_number, ifsc, bank_name } — any missing
// field comes back as an empty string.

export function parseLegacyBankDetails(txt) {
    const s = String(txt || "");
    const pick = (re) => {
        const m = s.match(re);
        return m ? m[1].trim().replace(/[,;.]+$/, "") : "";
    };

    return {
        // "Account Name: Foo Bar" or "Holder: Foo" or "Name: Foo".
        // Stop at common delimiters or the next labelled field, so we
        // don't slurp the whole rest of the line into the name.
        account_name: pick(
            /(?:Account\s*(?:Holder\s*)?Name|Holder|Acct?\.?\s*Name|Name)\s*[:\-]?\s*([A-Za-z][A-Za-z .'\-]{1,80}?)(?=\s*(?:,|;|\n|A\/?C|Acc|Account\b|IFSC|Bank|$))/i
        ),
        // "Account: 12345" / "A/C No: 12345" / "Acct No 12345" — must end
        // before the next non-digit cluster so a comma + IFSC after it
        // doesn't get absorbed.
        account_number: pick(
            /(?:A\/?C(?:\s*No\.?)?|Acc(?:t|ount)?\.?(?:\s*No\.?)?|Account\s*No\.?)\s*[:\-]?\s*([0-9][0-9\s]{4,30}?)(?=\s*(?:,|;|\n|IFSC|Bank|$))/i
        ),
        // IFSC codes are exactly 11 chars: 4 letters + "0" + 6 alphanum,
        // but tolerant: 6+ alphanum after the IFSC label is enough.
        ifsc: pick(
            /IFSC(?:\s*Code)?\s*[:\-]?\s*([A-Z][A-Z0-9]{5,15})/i
        ),
        // Bank name — eat letters, spaces, ampersand, dot, parens, hyphen.
        // Stop on comma / semicolon / next labelled field / EOL.
        bank_name: pick(
            /Bank(?:\s*Name)?\s*[:\-]?\s*([A-Za-z][A-Za-z &.'()\-]{1,60}?)(?=\s*(?:,|;|\n|A\/?C|Acc|Account\b|IFSC|$))/i
        ),
    };
}

// Resolve bank-display rows from settings — prefer structured fields,
// fall back to parsing the legacy `bank_details` blob if those are empty,
// and finally fall back to the UPI Payee Name for the Account Name row
// (individual landlords almost always share the name across UPI + bank).
// Returns [{ label, key, value }] with only filled rows. Empty list means
// the landlord truly hasn't configured anything.
export function getBankRows(settings) {
    const structured = [
        { label: "Account Name",   key: "bank_account_name",   value: settings?.bank_account_name },
        { label: "Account Number", key: "bank_account_number", value: settings?.bank_account_number },
        { label: "IFSC",           key: "bank_ifsc",           value: settings?.bank_ifsc },
        { label: "Bank",           key: "bank_name",           value: settings?.bank_name },
    ];
    const allEmpty = structured.every(r => !r.value || !String(r.value).trim());
    if (allEmpty && settings?.bank_details) {
        const p = parseLegacyBankDetails(settings.bank_details);
        structured[0].value = p.account_name;
        structured[1].value = p.account_number;
        structured[2].value = p.ifsc;
        structured[3].value = p.bank_name;
    }
    // Account-Name fallback: if it's still empty but the landlord has set
    // a UPI Payee Name, surface that here so tenants always see *some* name
    // for the bank transfer (same person in 99% of cases).
    if (!structured[0].value || !String(structured[0].value).trim()) {
        if (settings?.upi_name && String(settings.upi_name).trim()) {
            structured[0].value = String(settings.upi_name).trim();
        }
    }
    return structured.filter(r => r.value && String(r.value).trim());
}
